import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Ticket, Plus, Trash2, RefreshCw, Pencil, Handshake, Link2, UserCheck, UserPlus } from "lucide-react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { StatusBadge } from "../../components/StatusBadge";
import { AffiliateSettleDialog } from "./AffiliateSettleDialog";
import { AffiliateLinkDialog } from "./AffiliateLinkDialog";

const money = (v) => `$${(v ?? 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_STYLES = {
  unused: "bg-[#F7F5F2] text-[#6E675F] border-[#DDD7CF]",
  live: "bg-[#EAF2EC] text-[#2D6A4F] border-[#2D6A4F]/30",
  expired: "bg-[#FBEAEA] text-[#B42318] border-[#B42318]/30",
};

const EMPTY_FORM = { code: "", discount_type: "percent", discount_value: "", max_uses: "", expires_at: "", once_per_user: false, stackable: false, is_affiliate: false, affiliate_name: "", commission_type: "percent", commission_value: "" };

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

function SummaryCard({ label, value, sub, testid }) {
  return (
    <div className="border border-[#DDD7CF] rounded-lg bg-white p-5" data-testid={testid}>
      <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6E675F]">{label}</p>
      <p className="text-2xl font-medium text-[#1A1A1A] mt-1.5">{value}</p>
      {sub && <p className="text-xs text-[#6E675F] mt-1">{sub}</p>}
    </div>
  );
}

function CouponForm({ editing, onSaved, onCancelEdit, formRef }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (editing) {
      setForm({
        code: editing.code,
        discount_type: editing.discount_type,
        discount_value: String(editing.discount_value),
        max_uses: editing.max_uses ? String(editing.max_uses) : "",
        expires_at: toLocalInput(editing.expires_at),
        once_per_user: !!editing.once_per_user,
        stackable: !!editing.stackable,
        is_affiliate: !!editing.is_affiliate,
        affiliate_name: editing.affiliate_name || "",
        commission_type: editing.commission_type || "percent",
        commission_value: editing.commission_value ? String(editing.commission_value) : "",
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [editing]);

  const submit = async () => {
    const value = parseFloat(form.discount_value);
    if (!value || value <= 0) {
      toast.error("Enter a discount value greater than 0");
      return;
    }
    setSaving(true);
    const payload = {
      code: editing ? null : form.code.trim() || null,
      discount_type: form.discount_type,
      discount_value: value,
      max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      once_per_user: form.once_per_user,
      stackable: form.stackable,
      is_affiliate: form.is_affiliate,
      affiliate_name: form.affiliate_name.trim(),
      commission_type: form.commission_type,
      commission_value: parseFloat(form.commission_value) || 0,
    };
    try {
      if (editing) {
        const { data } = await api.put(`/admin/coupons/${editing.id}`, payload);
        toast.success(`Coupon ${data.code} updated`);
      } else {
        const { data } = await api.post("/admin/coupons", payload);
        toast.success(`Coupon ${data.code} created`);
      }
      setForm(EMPTY_FORM);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${editing ? "update" : "create"} coupon`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={formRef} className={`border rounded-lg bg-white p-6 mb-8 ${editing ? "border-[#8C7B6B] ring-1 ring-[#8C7B6B]/30" : "border-[#DDD7CF]"}`} data-testid="coupon-create-form">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] flex items-center gap-2">
          {editing ? <><Pencil className="w-4 h-4" /> Editing {editing.code}</> : <><Plus className="w-4 h-4" /> Generate a Coupon</>}
        </p>
        {editing && (
          <button data-testid="coupon-form-cancel-edit" onClick={onCancelEdit} className="text-xs text-[#6E675F] font-semibold hover:text-[#1A1A1A] transition-colors">
            Cancel
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 items-end">
        <div className="col-span-2 lg:col-span-1">
          <label className="text-xs text-[#6E675F] block mb-1.5">{editing ? "Code (locked)" : "Code (blank = auto)"}</label>
          <Input data-testid="coupon-form-code" value={form.code} disabled={!!editing} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="SUMMER20" className="uppercase disabled:opacity-70" />
        </div>
        <div>
          <label className="text-xs text-[#6E675F] block mb-1.5">Type</label>
          <select
            data-testid="coupon-form-type"
            value={form.discount_type}
            onChange={set("discount_type")}
            className="w-full h-9 border border-input rounded-md px-3 text-sm bg-white focus:outline-none focus:border-[#8C7B6B]"
          >
            <option value="percent">% off</option>
            <option value="fixed">$ off</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-[#6E675F] block mb-1.5">{form.discount_type === "percent" ? "Percent" : "Amount ($)"}</label>
          <Input data-testid="coupon-form-value" type="number" min="0" step="0.01" value={form.discount_value} onChange={set("discount_value")} placeholder={form.discount_type === "percent" ? "10" : "25.00"} />
        </div>
        <div>
          <label className="text-xs text-[#6E675F] block mb-1.5">Max uses (blank = ∞)</label>
          <Input data-testid="coupon-form-max-uses" type="number" min="1" step="1" value={form.max_uses} onChange={set("max_uses")} placeholder="Unlimited" />
        </div>
        <div>
          <label className="text-xs text-[#6E675F] block mb-1.5">Expires (blank = never)</label>
          <Input data-testid="coupon-form-expires" type="datetime-local" value={form.expires_at} onChange={set("expires_at")} />
        </div>
        <Button data-testid="coupon-form-submit" onClick={submit} disabled={saving} className="bg-[#8C7B6B] text-white hover:bg-[#75665A]">
          {saving ? "Saving…" : editing ? "Save Changes" : "Create Coupon"}
        </Button>
      </div>
      <label className="mt-4 flex items-center gap-2.5 cursor-pointer w-fit">
        <input
          type="checkbox"
          data-testid="coupon-form-once-per-user"
          checked={form.once_per_user}
          onChange={(e) => setForm((f) => ({ ...f, once_per_user: e.target.checked }))}
          className="accent-[#8C7B6B] w-4 h-4"
        />
        <span className="text-sm text-[#1A1A1A]">Limit to one use per customer</span>
        <span className="text-xs text-[#6E675F]">— each account can redeem this code only once</span>
      </label>
      <label className="mt-2 flex items-center gap-2.5 cursor-pointer w-fit">
        <input
          type="checkbox"
          data-testid="coupon-form-stackable"
          checked={form.stackable}
          onChange={(e) => setForm((f) => ({ ...f, stackable: e.target.checked }))}
          className="accent-[#8C7B6B] w-4 h-4"
        />
        <span className="text-sm text-[#1A1A1A]">Stackable</span>
        <span className="text-xs text-[#6E675F]">— can be combined with other coupons in the same order (overrides the one-coupon rule)</span>
      </label>
      <label className="mt-2 flex items-center gap-2.5 cursor-pointer w-fit">
        <input
          type="checkbox"
          data-testid="coupon-form-affiliate"
          checked={form.is_affiliate}
          onChange={(e) => setForm((f) => ({ ...f, is_affiliate: e.target.checked }))}
          className="accent-[#8C7B6B] w-4 h-4"
        />
        <span className="text-sm text-[#1A1A1A]">Affiliate code</span>
        <span className="text-xs text-[#6E675F]">— track sales attributed to a partner and the commission they've earned</span>
      </label>
      {form.is_affiliate && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3 p-4 bg-[#F7F5F2] border border-[#DDD7CF] rounded-lg" data-testid="affiliate-fields">
          <div>
            <label className="text-xs text-[#6E675F] block mb-1.5">Affiliate name</label>
            <Input data-testid="coupon-form-affiliate-name" value={form.affiliate_name} onChange={set("affiliate_name")} placeholder="Jane Doe / @janedoe" />
          </div>
          <div>
            <label className="text-xs text-[#6E675F] block mb-1.5">Commission type</label>
            <select
              data-testid="coupon-form-commission-type"
              value={form.commission_type}
              onChange={set("commission_type")}
              className="w-full h-9 border border-input rounded-md px-3 text-sm bg-white focus:outline-none focus:border-[#8C7B6B]"
            >
              <option value="percent">% of attributed sales (cash)</option>
              <option value="points_per_dollar">Points per $1 of sales (product credit)</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-[#6E675F] block mb-1.5">{form.commission_type === "percent" ? "Commission %" : "Points per $1"}</label>
            <Input data-testid="coupon-form-commission-value" type="number" min="0" step="0.1" value={form.commission_value} onChange={set("commission_value")} placeholder="10" />
          </div>
        </div>
      )}
    </div>
  );
}

function CouponOrders({ coupon }) {
  const [orders, setOrders] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/admin/coupons/${coupon.id}/orders`)
      .then(({ data }) => setOrders(data.orders))
      .catch(() => {
        toast.error("Failed to load coupon orders");
        setOrders([]);
      });
  }, [coupon.id]);

  if (!orders) {
    return <p className="text-xs text-[#6E675F] py-3 px-4">Loading orders…</p>;
  }
  if (orders.length === 0) {
    return (
      <p className="text-xs text-[#6E675F] py-3 px-4" data-testid={`coupon-orders-empty-${coupon.code}`}>
        No orders have used {coupon.code} yet.
      </p>
    );
  }
  return (
    <div className="px-4 py-3" data-testid={`coupon-orders-${coupon.code}`}>
      <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6E675F] mb-2">
        Orders that used {coupon.code} ({orders.length})
      </p>
      <div className="space-y-1.5">
        {orders.map((o) => (
          <button
            type="button"
            key={o.id}
            onClick={() => navigate(`/admin?tab=orders&q=${encodeURIComponent(o.order_number)}`)}
            title={`Open ${o.order_number} in Orders`}
            className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 text-xs bg-white border border-[#DDD7CF] rounded-md px-3 py-2 text-left cursor-pointer hover:border-[#8C7B6B] transition-colors"
            data-testid={`coupon-order-row-${o.order_number}`}
          >
            <span className="font-mono font-semibold text-[#8C7B6B] underline underline-offset-2">{o.order_number}</span>
            <span className="text-[#6E675F]">{o.customer_name || o.email}</span>
            <span className="text-[#6E675F]">{new Date(o.created_at).toLocaleDateString("en-CA", { dateStyle: "medium" })}</span>
            <StatusBadge status={o.status} />
            <span className="ml-auto text-[#B42318]">−{money(o.coupon_discount)}</span>
            <span className="font-medium text-[#1A1A1A]">{money(o.total)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export const AdminCoupons = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [deletingId, setDeletingId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [payoutsFor, setPayoutsFor] = useState({});
  const [settling, setSettling] = useState(null);
  const [linking, setLinking] = useState(null);
  const formRef = useRef(null);

  const startEdit = (c) => {
    setEditing(c);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const load = () => {
    api.get("/admin/coupons")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load coupons"))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}? Customers will no longer be able to use it.`)) return;
    setDeletingId(c.id);
    try {
      await api.delete(`/admin/coupons/${c.id}`);
      toast.success(`Coupon ${c.code} deleted`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete coupon");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading || !data) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#8C7B6B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { coupons, summary } = data;
  const affiliates = coupons.filter((c) => c.is_affiliate);
  const visible = filter === "all" ? coupons : coupons.filter((c) => c.status === filter);

  const loadPayouts = async (c, force = false) => {
    if (payoutsFor[c.id] && !force) {
      setPayoutsFor((prev) => ({ ...prev, [c.id]: null }));
      return;
    }
    try {
      const { data: d } = await api.get(`/admin/coupons/${c.id}/payouts`);
      setPayoutsFor((prev) => ({ ...prev, [c.id]: d.payouts }));
    } catch {
      toast.error("Failed to load payout history");
    }
  };

  const afterAffiliateChange = (c) => {
    setSettling(null);
    setLinking(null);
    load();
    if (payoutsFor[c.id]) loadPayouts(c, true);
  };
  const copyRefLink = (c) => {
    const link = `${window.location.origin}/?ref=${encodeURIComponent(c.code)}`;
    navigator.clipboard.writeText(link)
      .then(() => toast.success(`Affiliate link for ${c.code} copied`))
      .catch(() => window.prompt("Copy this link:", link));
  };

  const filters = [
    { key: "all", label: `All (${summary.total})` },
    { key: "unused", label: `Unused (${summary.unused})` },
    { key: "live", label: `Live (${summary.live})` },
    { key: "expired", label: `Expired (${summary.expired})` },
  ];

  return (
    <div data-testid="admin-coupons-section">
      <div className="flex items-center justify-between mb-5">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] flex items-center gap-2">
          <Ticket className="w-4 h-4" /> Coupon Codes
        </p>
        <button onClick={load} data-testid="coupons-refresh-button" className="text-xs text-[#8C7B6B] font-semibold inline-flex items-center gap-1 hover:text-[#1A1A1A] transition-colors">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="coupons-summary-grid">
        <SummaryCard label="Total Redemptions" value={summary.total_redemptions} testid="coupons-stat-redemptions" />
        <SummaryCard label="Discount Given" value={money(summary.total_discount_given)} sub="across all orders" testid="coupons-stat-discount" />
        <SummaryCard label="Revenue w/ Coupons" value={money(summary.coupon_revenue)} sub="paid orders only" testid="coupons-stat-revenue" />
        <SummaryCard label="Coupons" value={summary.total} sub={`${summary.live} live · ${summary.unused} unused · ${summary.expired} expired`} testid="coupons-stat-count" />
      </div>

      <CouponForm editing={editing} formRef={formRef} onSaved={() => { setEditing(null); load(); }} onCancelEdit={() => setEditing(null)} />

      {affiliates.length > 0 && (
        <div className="mb-8" data-testid="affiliates-section">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] flex items-center gap-2 mb-3">
            <Handshake className="w-4 h-4" /> Affiliates ({affiliates.length})
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {affiliates.map((c) => {
              const pts = c.commission_unit === "points";
              const fmt = (v) => (pts ? `${(v || 0).toLocaleString()} pts` : money(v || 0));
              const lifetime = c.commission_earned;
              const history = payoutsFor[c.id];
              return (
                <div key={c.id} className="border border-[#8C7B6B]/40 rounded-lg bg-white p-5" data-testid={`affiliate-card-${c.code}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-[#1A1A1A] truncate">{c.affiliate_name || c.code}</p>
                    <span className="font-mono text-xs font-bold text-[#8C7B6B] shrink-0">{c.code}</span>
                  </div>
                  <button
                    type="button"
                    data-testid={`affiliate-link-account-${c.code}`}
                    onClick={() => setLinking(c)}
                    className={`mt-2 inline-flex items-center gap-1.5 text-[11px] rounded-none px-2.5 py-1 border transition-colors ${c.affiliate_user_id ? "bg-[#EAF2EC] text-[#2D6A4F] border-[#2D6A4F]/30 hover:bg-[#DCEBE0]" : "text-[#8A6D2F] bg-[#F6F1E7] border-[#D4B36A]/50 hover:bg-[#F0E6D2]"}`}
                    title={c.affiliate_user_id ? "Change linked account" : "Link a customer account"}
                  >
                    {c.affiliate_user_id ? <><UserCheck className="w-3 h-3" /> {c.affiliate_user_email}</> : <><UserPlus className="w-3 h-3" /> Link customer account</>}
                  </button>
                  <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                    <div>
                      <p className="text-lg font-medium text-[#1A1A1A]">{c.paid_orders}</p>
                      <p className="text-[10px] uppercase tracking-wide text-[#6E675F]">Paid sales</p>
                    </div>
                    <div>
                      <p className="text-lg font-medium text-[#1A1A1A]">{money(c.revenue)}</p>
                      <p className="text-[10px] uppercase tracking-wide text-[#6E675F]">Revenue</p>
                    </div>
                    <div>
                      <p className={`text-lg font-medium ${(c.commission_outstanding || 0) > 0 ? "text-[#2D6A4F]" : "text-[#9A9088]"}`} data-testid={`affiliate-outstanding-${c.code}`}>
                        {fmt(c.commission_outstanding)}
                      </p>
                      <p className="text-[10px] uppercase tracking-wide text-[#6E675F]">Owed now</p>
                    </div>
                  </div>
                  <p className="text-[11px] text-[#6E675F] mt-3" data-testid={`affiliate-lifetime-${c.code}`}>
                    {pts ? `${c.commission_value} pts per $1` : `${c.commission_value}% of sales`} · lifetime {fmt(lifetime)} · settled {fmt(c.commission_settled)}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <button
                      data-testid={`settle-affiliate-${c.code}`}
                      onClick={() => setSettling(c)}
                      disabled={!(c.commission_outstanding > 0)}
                      className="flex-1 text-[11px] tracking-[0.12em] uppercase font-semibold bg-[#2D6A4F] text-white rounded-md px-3 py-2 hover:bg-[#245740] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Mark Settled
                    </button>
                    <button
                      data-testid={`affiliate-payouts-toggle-${c.code}`}
                      onClick={() => loadPayouts(c)}
                      className="text-[11px] tracking-[0.12em] uppercase font-semibold text-[#8C7B6B] border border-[#8C7B6B] rounded-md px-3 py-2 hover:bg-[#8C7B6B] hover:text-white transition-colors"
                    >
                      Payouts ({c.payout_count || 0})
                    </button>
                    <button
                      data-testid={`copy-affiliate-link-${c.code}`}
                      onClick={() => copyRefLink(c)}
                      title={`Copy shareable link — auto-applies ${c.code} at checkout`}
                      aria-label={`Copy affiliate link for ${c.code}`}
                      className="text-[#8C7B6B] border border-[#8C7B6B] rounded-md px-2.5 py-2 hover:bg-[#8C7B6B] hover:text-white transition-colors"
                    >
                      <Link2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {history && (
                    <div className="mt-3 border-t border-[#EFEBE6] pt-3 space-y-2 max-h-44 overflow-y-auto" data-testid={`affiliate-payout-log-${c.code}`}>
                      {history.length === 0 && <p className="text-xs text-[#6E675F]">No payouts recorded yet.</p>}
                      {history.map((p) => (
                        <div key={p.id} className="text-xs flex items-start justify-between gap-2" data-testid={`payout-row-${p.id}`}>
                          <div className="min-w-0">
                            <p className="text-[#1A1A1A] font-medium">
                              {p.unit === "points" ? `${(p.amount || 0).toLocaleString()} pts` : money(p.amount)}
                              <span className="text-[#6E675F] font-normal"> · {new Date(p.created_at).toLocaleDateString("en-CA")}</span>
                            </p>
                            <p className={`text-[10px] font-semibold uppercase tracking-wide ${p.method === "points" ? "text-[#2D6A4F]" : "text-[#6E675F]"}`} data-testid={`payout-method-${p.id}`}>
                              {p.method === "points" ? `Reward points · ${(p.points_credited || 0).toLocaleString()} pts credited` : "Paid in dollars"}
                            </p>
                            {p.note && <p className="text-[#6E675F] truncate">{p.note}</p>}
                          </div>
                          <span className="text-[#9A9088] shrink-0">{p.paid_by?.split("@")[0]}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-6">
        {filters.map((f) => (
          <button
            key={f.key}
            data-testid={`coupons-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`text-xs tracking-[0.1em] uppercase font-semibold px-4 py-2 rounded-none border transition-colors ${
              filter === f.key ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "text-[#6E675F] border-[#DDD7CF] hover:border-[#8C7B6B]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <p className="text-sm text-[#6E675F] py-10 text-center border border-[#DDD7CF] rounded-lg bg-white" data-testid="coupons-empty">
          {filter === "all" ? "No coupons yet — generate your first one above." : `No ${filter} coupons.`}
        </p>
      ) : (
        <div className="border border-[#DDD7CF] rounded-lg bg-white overflow-x-auto">
          <Table data-testid="admin-coupons-table">
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Discount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Uses</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Discount Given</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((c) => (
                <React.Fragment key={c.id}>
                <TableRow
                  data-testid={`coupon-row-${c.code}`}
                  onClick={() => setExpandedId((id) => (id === c.id ? null : c.id))}
                  className={`cursor-pointer ${expandedId === c.id ? "bg-[#F7F5F2]" : ""}`}
                >
                  <TableCell className="font-mono font-semibold text-[#1A1A1A]">
                    {c.code}
                    {c.is_affiliate && (
                      <span className="block text-[10px] text-[#7C3AED] font-semibold font-sans" data-testid={`coupon-affiliate-badge-${c.code}`}>
                        Affiliate{c.affiliate_name ? ` · ${c.affiliate_name}` : ""}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {c.discount_type === "percent" ? `${c.discount_value}% off` : `${money(c.discount_value)} off`}
                    {c.stackable && (
                      <span className="block text-[10px] text-[#2D6A4F] font-semibold" data-testid={`coupon-stackable-badge-${c.code}`}>Stackable</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex text-[11px] font-bold uppercase tracking-wide border rounded-none px-2.5 py-1 ${STATUS_STYLES[c.status]}`} data-testid={`coupon-status-${c.code}`}>
                      {c.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-right" data-testid={`coupon-uses-${c.code}`}>
                    {c.uses_count}{c.max_uses ? ` / ${c.max_uses}` : " / ∞"}
                    {c.once_per_user && (
                      <span className="block text-[10px] text-[#8A6D2F] font-semibold" data-testid={`coupon-once-badge-${c.code}`}>1 / customer</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-[#6E675F]">
                    {c.expires_at ? new Date(c.expires_at).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }) : "Never"}
                  </TableCell>
                  <TableCell className="text-right">{money(c.discount_given)}</TableCell>
                  <TableCell className="text-right font-medium">{money(c.revenue)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <button
                      data-testid={`coupon-edit-${c.code}`}
                      onClick={(e) => { e.stopPropagation(); startEdit(c); }}
                      className="text-[#8C7B6B] hover:bg-[#EFEBE6] rounded-md p-2 transition-colors"
                      title={`Edit ${c.code}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`coupon-delete-${c.code}`}
                      onClick={(e) => { e.stopPropagation(); remove(c); }}
                      disabled={deletingId === c.id}
                      className="text-[#B42318] hover:bg-[#FBEAEA] rounded-md p-2 transition-colors disabled:opacity-50"
                      title={`Delete ${c.code}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </TableCell>
                </TableRow>
                {expandedId === c.id && (
                  <TableRow className="bg-[#F7F5F2] hover:bg-[#F7F5F2]">
                    <TableCell colSpan={8} className="p-0">
                      <CouponOrders coupon={c} />
                    </TableCell>
                  </TableRow>
                )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {settling && <AffiliateSettleDialog key={settling.id} coupon={settling} onClose={() => setSettling(null)} onDone={() => afterAffiliateChange(settling)} />}
      {linking && <AffiliateLinkDialog key={linking.id} coupon={linking} onClose={() => setLinking(null)} onDone={() => afterAffiliateChange(linking)} />}
    </div>
  );
};
